/**
 * Correlation ID utilities for end-to-end telemetry tracing
 * Story: 116-123 - Instrument API and error telemetry
 *
 * Generates or propagates correlation IDs across API requests and telemetry
 * events. Falls back to a generated UUID when no correlation ID is present.
 */

/**
 * HTTP request/response header used to carry the correlation ID.
 */
export const CORRELATION_HEADER = 'X-Correlation-ID'

/**
 * UUID v4 regex pattern used for validation.
 */
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * Generate a new UUID v4 correlation ID.
 * Uses `crypto.randomUUID()` when available; falls back to a RFC 4122-compliant
 * polyfill for environments without the native API.
 *
 * @returns A UUID v4 string
 */
export function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: RFC 4122 UUID v4 via Math.random()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get or create a correlation ID.
 * Returns the provided `existing` ID when it is a non-empty string AND a valid
 * UUID v4; otherwise generates a fresh one.  Malformed header values are
 * discarded to prevent propagating invalid IDs downstream.
 *
 * @param existing - Optional correlation ID from a request header or context
 * @returns Valid UUID v4 correlation ID (existing validated or newly generated)
 */
export function getOrCreateCorrelationId(existing?: string | null): string {
  if (typeof existing === 'string' && existing.trim().length > 0) {
    const trimmed = existing.trim()
    if (isValidCorrelationId(trimmed)) {
      return trimmed
    }
  }
  return generateCorrelationId()
}

/**
 * Validate that a string looks like a valid UUID v4 correlation ID.
 *
 * @param id - The string to validate
 * @returns true if `id` matches the UUID v4 format
 */
export function isValidCorrelationId(id: string): boolean {
  return UUID_V4_PATTERN.test(id)
}
